import React from 'react';
import { Form, Input, InputNumber, Select, DatePicker, Upload, Switch } from 'antd';
import { FormItemInputContext } from 'antd/lib/form/context';
import AntIcon from '../components/AntIcon';
import moment from 'moment';

/** When a parent Form.Item passes `value={undefined}` to the context (React 18), Input/Select read `.status` on undefined and throw. */
const FORM_ITEM_INPUT_FALLBACK = {
  status: '',
  hasFeedback: false,
  feedbackIcon: null,
  isFormItemInput: true,
};

export const wrapFormItemInput = (node) => (
  <FormItemInputContext.Provider value={FORM_ITEM_INPUT_FALLBACK}>
    {node}
  </FormItemInputContext.Provider>
);

export const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 6 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 18 },
  },
};

export const formItemLayoutWithoutLabel = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
    sm: { span: 18, offset: 6 },
  },
};

export const formItemLayoutInputOnly = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
  },
};

export const getValidationState = ({ error, warning, touched }) => {
  if (touched && error) return "error";
  if (touched && warning) return "warning";
  return "success";
};

const render = ({ input, label, extra, meta, required, inputOnly, layout, ...props }, renderInput) => {
  const id = `${meta.form}_${input.name}`;
  const inputProps = { ...input, id, ...props };
  const finalLayout = inputOnly ? formItemLayoutInputOnly :
    (layout || (label ? formItemLayout : formItemLayoutWithoutLabel));

  return (
    <Form.Item
      {...finalLayout}
      required={required}
      label={inputOnly ? null : label}
      extra={extra}
      validateStatus={getValidationState(meta)}
      help={meta.touched ? meta.error : null}
    >
      {renderInput(inputProps)}
    </Form.Item>
  );
};

export const renderInput = (props) => render(props, (inputProps) =>
  wrapFormItemInput(<Input {...inputProps} />),
);

export const renderNumberInput = (props) => render(props, ({ onBlur, value, ...inputProps }) =>
  wrapFormItemInput(
    <InputNumber {...inputProps} value={value} onBlur={() => onBlur()} />,
  ),
);

export const renderSelect = ({ options, ...props }) => render(props, ({ value, onBlur, ...inputProps }) =>
  wrapFormItemInput(
    // Ant Design Select passes a native blur event to onBlur. redux-form treats that as a value
    // update and can set the field to '', clearing the selection when focusing another field.
    (
      <Select
        {...inputProps}
        value={value === undefined || value === null ? undefined : value}
        onBlur={() => onBlur()}
      >
        {options.map(({ value: optValue, label }, index) => (
          <Select.Option
            key={optValue !== undefined && optValue !== null && optValue !== '' ? String(optValue) : `empty-${index}`}
            value={optValue}
          >
            {label}
          </Select.Option>
        ))}
      </Select>
    ),
  ),
);

export const renderMultiSelect = ({ options, createPostTag, ...props }) => render(props, ({ value, ...inputProps}) => {
  const { Option } = Select;

  const children = []
  options.items && options.items.forEach((item) => {
    children.push(<Option key={item._id}>{item.title}</Option>);
  })

  const valuesList = []
  if (value) {
    value.forEach((item) => {
      if (typeof(item) === "object") {
        valuesList.push(item.tag ? item.tag._id : null)
      } else {
        valuesList.push(item)
      }
    })
  }

  return wrapFormItemInput(
    <Select {...inputProps} mode="multiple" value={valuesList}>
      {children}
    </Select>,
  );
});

export const renderDateTime = (props) => render(props, ({ value, onChange, pickerFormat, ...inputProps }) => {
  const toMoment = (v) => {
    if (v == null || v === '') return null;
    const m = pickerFormat ? moment.utc(v, pickerFormat, true) : moment.utc(v);
    return m.isValid() ? m : null;
  };

  const momentValue = toMoment(value) || undefined;

  return wrapFormItemInput(
    <DatePicker
      {...inputProps}
      format={pickerFormat}
      allowClear
      onChange={(date, str) => {
        if (date == null || str === '' || (str != null && String(str).trim() === '')) {
          onChange(null);
          return;
        }
        const next = pickerFormat ? moment.utc(str, pickerFormat, true) : moment.utc(str);
        if (!next.isValid()) {
          onChange(null);
          return;
        }
        onChange(next.format());
      }}
      value={momentValue}
    />,
  );
});

export const renderLabel = (props) => render(props, ({ value }) => {
  return (
    <span className="ant-form-text">{value}</span>
  );
});

export const renderTextarea = (props) => render(props, (inputProps) =>
  wrapFormItemInput(<Input.TextArea {...inputProps} />),
);

export const renderInputUpload = (props) => render(props, (inputProps) => {
  return (
    <Upload {...inputProps}>
      <div>
        <AntIcon type="plus" />
        <div className="ant-upload-text">Upload</div>
      </div>
    </Upload>
  );
});

export const renderRangePicker = (props) => render(props, ({ value, onChange, ...inputProps }) =>
  wrapFormItemInput(
    <DatePicker.RangePicker
      {...inputProps}
      onChange={(date, str) => onChange(str)}
      value={value ? [moment(value[0]), moment(value[1])] : undefined}
    />,
  ),
);

export const renderSwitch = ({ inline, ...props }) => {
  const layout = inline ? {
    labelCol: { xs: 16 },
    wrapperCol: { xs: 8 },
  } : null;

  return render({ ...props, layout }, ({ value, ...inputProps }) => {
    return <Switch className="is-right" {...inputProps} checked={value === true} />
  });
};
